import { SampleStatusEnum } from "src/app/enums/sampleStatus.enum";
import { DateFilterTypeEnum } from "src/app/enums/dateFilterType.enum";
import { getEnumLookup } from "src/app/_helpers/enum-helper";
import nameOf from "src/app/shared/form/nameOf.helper";
import { SampleStatusYearPreset } from "src/app/enums/sampleStatusYearPreset.enum";

export interface SampleStatusFilterValues {
    selectedStatuses: number[];
    dateFilterType: number | undefined;
    customStartDate: string | null;
    customEndDate: string | null;
}

export const SAMPLE_STATUS_FILTER_DEFAULTS: SampleStatusFilterValues = {
    selectedStatuses: [],
    dateFilterType: undefined,
    customStartDate: null,
    customEndDate: null,
};

const statusesKey = nameOf<SampleStatusFilterValues>((x) => x.selectedStatuses);
const dateFilterKey = nameOf<SampleStatusFilterValues>((x) => x.dateFilterType);
const customStartKey = nameOf<SampleStatusFilterValues>((x) => x.customStartDate);
const customEndKey = nameOf<SampleStatusFilterValues>((x) => x.customEndDate);

export function configToFilterValues(
    config: Record<string, unknown>,
): SampleStatusFilterValues {
    return {
        selectedStatuses: (config[statusesKey] as number[]) ?? [],
        dateFilterType: config[dateFilterKey] as number | undefined,
        customStartDate: typeof config[customStartKey] === 'string' ? (config[customStartKey] as string) : null,
        customEndDate: typeof config[customEndKey] === 'string' ? (config[customEndKey] as string) : null,
    };
}

export function filterValuesToConfig(
    values: SampleStatusFilterValues,
): Record<string, unknown> {
    const config: Record<string, unknown> = {};
    if (values.selectedStatuses.length > 0)
        config[statusesKey] = values.selectedStatuses;
    if (values.dateFilterType != null)
        config[dateFilterKey] = values.dateFilterType;
    if (values.customStartDate)
        config[customStartKey] = values.customStartDate;
    if (values.customEndDate)
        config[customEndKey] = values.customEndDate;
    return config;
}

export const EXCLUDED_STATUSES = new Set([
    SampleStatusEnum.ApprovedLetterSent,
    SampleStatusEnum.VoidLetterSent,
]);

export const INCLUDED_DATE_FILTERS = new Set<number>([
    DateFilterTypeEnum.AllDates,
    DateFilterTypeEnum.ThisYear,
    DateFilterTypeEnum.ThisQuarter,
    DateFilterTypeEnum.ThisMonth,
    DateFilterTypeEnum.LastYear,
    DateFilterTypeEnum.LastQuarter,
    DateFilterTypeEnum.LastMonth,
    SampleStatusYearPreset.Last2Years,
    SampleStatusYearPreset.Last3Years,
    SampleStatusYearPreset.Last5Years,
    DateFilterTypeEnum.Custom,
]);

export const DATE_LABEL_OVERRIDES: Record<number, string> = {
    [DateFilterTypeEnum.AllDates]: "All Dates",
    [DateFilterTypeEnum.ThisMonth]: "This Month",
    [DateFilterTypeEnum.ThisQuarter]: "This Quarter",
    [DateFilterTypeEnum.ThisYear]: "Year to Date",
    [DateFilterTypeEnum.LastMonth]: "Last Month",
    [DateFilterTypeEnum.LastQuarter]: "Last Quarter",
    [DateFilterTypeEnum.LastYear]: "Last 12 Months",
    [DateFilterTypeEnum.Custom]: "Custom",
    [SampleStatusYearPreset.Last2Years]: "Last 2 Years",
    [SampleStatusYearPreset.Last3Years]: "Last 3 Years",
    [SampleStatusYearPreset.Last5Years]: "Last 5 Years",
};

export const STATUS_OPTIONS = getEnumLookup(SampleStatusEnum)
    .filter(({ id }) => !EXCLUDED_STATUSES.has(id))
    .map(({ id, name }) => ({ value: id, label: name }));

const DATE_FILTER_ORDER: number[] = [
    DateFilterTypeEnum.AllDates,
    DateFilterTypeEnum.ThisMonth,
    DateFilterTypeEnum.ThisQuarter,
    DateFilterTypeEnum.ThisYear,
    DateFilterTypeEnum.LastMonth,
    DateFilterTypeEnum.LastQuarter,
    DateFilterTypeEnum.LastYear,
    SampleStatusYearPreset.Last2Years,
    SampleStatusYearPreset.Last3Years,
    SampleStatusYearPreset.Last5Years,
    DateFilterTypeEnum.Custom,
];

export const DATE_RANGE_OPTIONS = DATE_FILTER_ORDER
    .filter((id) => INCLUDED_DATE_FILTERS.has(id))
    .map((id) => ({
        value: id,
        label: DATE_LABEL_OVERRIDES[id] || DateFilterTypeEnum[id],
    }));

export function resolveDateFilterRange(filterType: number, now: Date = new Date()): { start: Date; end: Date } | null {
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);
    switch (filterType) {
        case DateFilterTypeEnum.ThisYear:
            return { start: new Date(currentYear, 0, 1), end: now };
        case DateFilterTypeEnum.ThisQuarter:
            return { start: new Date(currentYear, currentQuarter * 3, 1), end: now };
        case DateFilterTypeEnum.ThisMonth:
            return { start: new Date(currentYear, currentMonth, 1), end: now };
        case DateFilterTypeEnum.LastYear: {
            const start = new Date(now);
            start.setFullYear(start.getFullYear() - 1);
            return { start, end: now };
        }
        case DateFilterTypeEnum.LastQuarter: {
            const lastQuarterStart = new Date(currentYear, (currentQuarter - 1) * 3, 1);
            const lastQuarterEnd = new Date(currentYear, currentQuarter * 3, 0, 23, 59, 59);
            return { start: lastQuarterStart, end: lastQuarterEnd };
        }
        case DateFilterTypeEnum.LastMonth: {
            const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
            const lastMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59);
            return { start: lastMonthStart, end: lastMonthEnd };
        }
        case SampleStatusYearPreset.Last2Years:
            return { start: new Date(currentYear - 2, 0, 1), end: new Date(currentYear - 1, 11, 31, 23, 59, 59) };
        case SampleStatusYearPreset.Last3Years:
            return { start: new Date(currentYear - 3, 0, 1), end: new Date(currentYear - 1, 11, 31, 23, 59, 59) };
        case SampleStatusYearPreset.Last5Years:
            return { start: new Date(currentYear - 5, 0, 1), end: new Date(currentYear - 1, 11, 31, 23, 59, 59) };
        default:
            return null;
    }
}
